import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, TextStyle, ViewStyle } from 'react-native';
import Animated from 'react-native-reanimated';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { Outcome } from '@/types/api.generated';
import { getColorByProjType } from '@/utils/getProjectionsTypes';
import { isProjectionAndOutcomeSelected } from '@/utils/projections';

import { BetslipPick } from '../betslip-pickem/types';
import { checkIfDiscountedProjection } from '../lobby/utils/lineupsUtils';
import { usePickButtonAnimations } from './hooks/use-pick-buttons-animation';

const PickButton = ({
    outcome,
    selected,
    onPress,
    disabled,
    style,
    textStyle,
}: {
    outcome: Outcome;
    selected: boolean;
    onPress: () => void;
    disabled?: boolean;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
}) => {
    const { t } = useTranslation(['betslip_pickem']);
    const label = t(`${outcome}`);
    const testId = `${outcome}-button`;
    return (
        <Button
            onPress={onPress}
            analyticsTag={label}
            style={[
                styles.button,
                {
                    backgroundColor: selected ? designSystem.colors.white : designSystem.colors.neutral600,
                },
                style,
            ]}
            label={
                <Text
                    numberOfLines={1}
                    ellipsizeMode={'clip'}
                    variant={'titleSmall'}
                    fontSize={14}
                    letterSpacing={-0.15}
                    style={[
                        {
                            color: selected ? designSystem.colors.gray8 : designSystem.colors.white,
                        },
                        textStyle,
                    ]}
                >
                    {label}
                </Text>
            }
            disabled={disabled}
            active={selected}
            testID={testId}
        />
    );
};

export const PickButtons = ({
    projection,
    playerPicked,
    disabled,
    makeSelection,
    removeSelection,
}: {
    projection: PlayerProjectionFragment;
    playerPicked?: BetslipPick;
    disabled?: boolean;
    removeSelection: () => void;
    makeSelection: (outcome: Outcome) => void;
}) => {
    const selectedMore = isProjectionAndOutcomeSelected(Outcome.More, projection, playerPicked);
    const selectedLess = isProjectionAndOutcomeSelected(Outcome.Less, projection, playerPicked);
    const selection = selectedMore ? Outcome.More : selectedLess ? Outcome.Less : undefined;
    const allowedOutcomes = projection.allowedOptions ?? [];

    const onPress = useCallback(
        (outcome: Outcome) => {
            if (disabled) {
                return;
            }
            const pressedOnMore = outcome === Outcome.More;
            const pressedOnLess = outcome === Outcome.Less;
            if ((pressedOnMore && selectedMore) || (pressedOnLess && selectedLess)) {
                removeSelection();
            } else {
                makeSelection(outcome);
            }
        },
        [disabled, makeSelection, removeSelection, selectedLess, selectedMore]
    );
    const hasMore = allowedOutcomes.some(outcome => outcome.outcome === Outcome.More);
    const hasLess = allowedOutcomes.some(outcome => outcome.outcome === Outcome.Less);

    const { moreButtonStyle, lessButtonStyle, buttonsContainerStyle } = usePickButtonAnimations(hasMore, hasLess);

    return (
        <Row>
            <Animated.View style={[buttonsContainerStyle, common.row]}>
                <Animated.View style={moreButtonStyle}>
                    <PickButton
                        outcome={Outcome.More}
                        selected={selection === Outcome.More}
                        onPress={() => onPress(Outcome.More)}
                        disabled={disabled}
                        {...getPickButtonStyles(Outcome.More, selectedMore, projection)}
                    />
                </Animated.View>

                <Animated.View style={lessButtonStyle}>
                    <PickButton
                        outcome={Outcome.Less}
                        selected={selection === Outcome.Less}
                        onPress={() => onPress(Outcome.Less)}
                        disabled={disabled}
                        {...getPickButtonStyles(Outcome.Less, selectedLess, projection)}
                    />
                </Animated.View>
            </Animated.View>
        </Row>
    );
};

type ButtonStylesResult = {
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
};

const getPickButtonStyles = (
    outcomeType: Outcome,
    isSelected: boolean,
    projection: PlayerProjectionFragment
): ButtonStylesResult => {
    const isDiscountProjection = checkIfDiscountedProjection(projection.type);
    const specialIncrease = (projection?.nonRegularPercentage && projection?.nonRegularPercentage > 0) || false;
    const projectionColor = getColorByProjType(projection.type, specialIncrease);
    const discountedProjectionColor = designSystem.colors[projectionColor];
    const discountedProjectionColorWithOpacity = withOpacity(discountedProjectionColor, '0.06');

    // Base styles
    const buttonStyle = outcomeType === Outcome.Less ? [{ marginLeft: 8 }] : [];

    // Add conditional styles for discounted projections
    if (isDiscountProjection) {
        return {
            style: [
                ...buttonStyle,
                {
                    backgroundColor: isSelected ? discountedProjectionColor : discountedProjectionColorWithOpacity,
                },
            ],
            textStyle: {
                color: isSelected ? designSystem.colors.gray8 : discountedProjectionColor,
            },
        };
    }

    // Regular styles
    return {
        style: buttonStyle.length ? buttonStyle : undefined,
        textStyle: undefined,
    };
};

const styles = StyleSheet.create({
    button: { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 10, borderCurve: 'continuous' },
    marginLeft: { marginLeft: 8 },
});
